/**
 * ChatDocuments - every file uploaded in this chat, in one place.
 *
 * A document attached three questions ago is still part of the chat's
 * context, but the only trace of it was a chip on a message scrolled far out
 * of view. This is the chat's document set: one button by the prompt bar,
 * one short list, each entry opening in the source viewer.
 *
 * Deliberately a popover rather than a floating panel - it is a lookup, not a
 * workspace, and it should not compete with history / document / settings for
 * a dock.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiFilesLight, PiPaperclipLight } from 'react-icons/pi';
import { Tooltip } from '@mui/material';
import { formatBytes } from '../../../utils/functions';
import { TurnAttachment, attachmentCitation } from '../attachments';

interface Props {
  documents: TurnAttachment[];
  /** Every citation in the chat - where an openable copy may be found. */
  citations?: any[];
  onOpen?: (citation: any) => void;
}

const ChatDocuments: React.FC<Props> = ({ documents, citations, onOpen }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // A popover that outlives the click somewhere else is a stuck overlay.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef} data-testid="chat-documents">
      <Tooltip title={t('Documents in this chat') as string} placement="top">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="chat-documents-toggle"
          data-count={documents.length}
          aria-expanded={open}
          aria-label={t('Documents in this chat')}
          className={clsx(
            'relative p-1.5 rounded-md text-fg-muted hover:text-fg-strong',
            'transition-colors duration-200 ease-slide',
            open && 'text-fg-strong bg-surface-sunken'
          )}
        >
          <PiFilesLight className="w-4 h-4" />
          {documents.length > 0 && (
            <span
              data-testid="chat-documents-count"
              className="absolute -top-0.5 -end-0.5 min-w-[14px] h-[14px] px-1 rounded-full
                         bg-champagne text-ink text-[9px] font-medium leading-[14px]"
            >
              {documents.length}
            </span>
          )}
        </button>
      </Tooltip>

      {open && (
        <div
          data-testid="chat-documents-panel"
          className="absolute bottom-full mb-2 start-0 z-40 w-72 glass rounded-lg shadow-sm
                     p-2 reveal-item"
        >
          <p className="px-1 pb-1.5 text-[11px] font-medium
                        text-fg-muted">
            {t('Documents in this chat')}
          </p>

          {documents.length === 0 ? (
            <p className="px-1 py-3 text-xs text-fg-muted" data-testid="chat-documents-empty">
              {t('No documents uploaded yet. Attach one to ground a question in it.')}
            </p>
          ) : (
            <ul className="space-y-0.5 max-h-64 overflow-y-auto" data-testid="chat-documents-list">
              {documents.map((document, i) => {
                const citation = onOpen ? attachmentCitation(document, citations) : null;
                const size = document.size ? formatBytes(document.size) : '';

                const body = (
                  <>
                    <PiPaperclipLight
                      className="w-3.5 h-3.5 text-fg-muted flex-shrink-0"
                      aria-hidden
                    />
                    <span className="flex-1 min-w-0 truncate">{document.name}</span>
                    {size && (
                      <span className="text-[10px] text-fg-muted flex-shrink-0">{size}</span>
                    )}
                  </>
                );

                const className = clsx(
                  'w-full flex items-center gap-2 px-1.5 py-1.5 rounded-md text-xs text-start',
                  'text-fg-body transition-colors duration-200 ease-slide',
                  citation ? 'hover:bg-surface-sunken hover:text-champagne' : 'opacity-70'
                );

                return (
                  <li key={document.uuid || `${document.name}-${i}`}>
                    {citation ? (
                      <button
                        type="button"
                        data-testid="chat-document"
                        data-openable="true"
                        onClick={() => { onOpen?.(citation); setOpen(false); }}
                        className={className}
                      >
                        {body}
                      </button>
                    ) : (
                      // Nothing to stream: no path of its own, and no
                      // citation to borrow one from. See `attachmentCitation`.
                      <span
                        data-testid="chat-document"
                        title={t('This document cannot be opened yet') as string}
                        className={className}
                      >
                        {body}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatDocuments;
